import {
  type GameState,
  type GameStateDelta,
  postDataTagMutation,
  postGameByGameIdSubstituteMutation,
} from "@aura-frontend/omnibus-az-client";
import { useMutation } from "@tanstack/react-query";
import { Button, toast } from "@win-reality/react-ui";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { getErrorDetail } from "@/utils/errorUtils";

import {
  incrementBullpenPitchCount,
  resetBullpenPitchCount,
  setBullpenPitcher,
} from "../../../store/slices/bullpen";
import { resetAllTags } from "../../../store/slices/session";
import { setStaleEvents, updateQueueEvent } from "../../../store/slices/socket";
import { selectRecentEventForReview } from "../../../store/slices/socket/selectors";
import { setModalState } from "../../../store/slices/view";
import type { RootState } from "../../../store/store";
import type { IPlayer } from "../../../store/types";
import {
  mapIPlayerToLineupEntry,
  sideTypeMap,
} from "../../../utils/api-helpers";
import { useFetchSavedLineupEntry } from "../../../utils/savedLineupEntry";
import { useUpdateGameState } from "../../../utils/useUpdateGameState";
import { SelectPitcherModal } from "../../modals/SelectPitcherModal";
import { ActivePlayerTile } from "../../shared/ActivePlayerTile";
import { TilesContainer } from "../../shared/TilesContainer";
import { VisualizationContainer } from "../../shared/VisualizationContainer";
import { BullpenPitchTypeButtons } from "./BullpenPitchTypeButtons";

export const BullpenView = () => {
  const dispatch = useDispatch();
  const { id: machineId } = useParams() as { id: string };
  const fetchSavedLineupEntry = useFetchSavedLineupEntry();
  const updateGameState = useUpdateGameState();
  const lastAutoSavedUuid = useRef<string | null>(null);

  const recentEvent = useSelector(selectRecentEventForReview);
  const tagData = useSelector((state: RootState) => state.session.tagData);
  const gameUuid = useSelector((state: RootState) => state.session.uuid);
  const gameState = useSelector((state: RootState) => state.game.gameState);
  const { currentPitcher, pitchCount } = useSelector(
    (state: RootState) => state.bullpen,
  );

  // Replace pitcher modal state
  const [replacePitcherOpen, setReplacePitcherOpen] = useState(false);

  // Mutation: Manual tag save
  const { mutate: saveManualTag, isPending: isManualSaving } = useMutation({
    ...postDataTagMutation(),
    onError: (error: Error) => {
      const errorMessage = getErrorDetail(error);
      toast(`Error saving pitch tag: ${errorMessage}`, { variant: "error" });
    },
  });

  // Mutation: Passive auto-save (separate instance for independent loading state)
  const { mutate: savePassiveTag } = useMutation({
    ...postDataTagMutation(),
    onError: (error: Error) => {
      console.error("Passive auto-save failed:", error);
    },
  });

  // Mutation: Pitcher substitution
  const { mutate: substitutePitcher, isPending: isSubstituting } = useMutation({
    mutationFn: postGameByGameIdSubstituteMutation().mutationFn,
    onError: (error: Error) => {
      const errorMessage = getErrorDetail(error);
      toast(`Error substituting pitcher: ${errorMessage}`, {
        variant: "error",
      });
    },
  });

  // Passive auto-save: When a new untagged event arrives, save it with empty metadata
  useEffect(() => {
    const eventUuid = recentEvent?.event_uuid;
    if (!eventUuid || !gameUuid || !machineId) return;
    if (eventUuid === lastAutoSavedUuid.current) return;
    if (recentEvent.tagData) return; // Already tagged
    if (!currentPitcher?.uuid) return; // No pitcher assigned yet — retry once one is picked

    lastAutoSavedUuid.current = eventUuid;

    const pitcherEntry = {
      externalId: currentPitcher.uuid,
      hitSide: sideTypeMap[currentPitcher.batHand] ?? "right",
      pitchSide: sideTypeMap[currentPitcher.throwHand] ?? "right",
      playerName:
        `${currentPitcher.lastName}, ${currentPitcher.firstName}`.trim(),
      position: "pitcher" as const,
    };

    savePassiveTag(
      {
        body: {
          payload: {
            event_uuid: eventUuid,
            event_time: recentEvent.event_time,
            machine_uuid: machineId,
            tag_data: {
              metadata: {} as GameStateDelta,
              gameState: {
                ...(gameState as GameState),
                currentPitcher: pitcherEntry,
              },
            },
          },
        },
      },
      {
        onSuccess: (data) => {
          dispatch(incrementBullpenPitchCount());
          if (data.gameState && data.game) {
            updateGameState(data.gameState, data.game);
          }
          dispatch(
            updateQueueEvent({
              ...recentEvent,
              // Mark this event as passively tagged (null = auto-saved, no manual tag data yet)
              tagData: null,
              gameState: data.gameState as IGameState,
            }),
          );
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentEvent?.event_uuid, currentPitcher?.uuid]);

  // Manual save: Sends tagData metadata + gameState with currentPitcher
  const handleManualSave = () => {
    if (!recentEvent?.event_uuid) {
      toast("No event to tag", { variant: "error" });
      return;
    }
    if (!currentPitcher?.uuid) {
      toast("No pitcher assigned", { variant: "error" });
      return;
    }

    const pitcherEntry = {
      externalId: currentPitcher.uuid,
      hitSide: sideTypeMap[currentPitcher.batHand] ?? "right",
      pitchSide: sideTypeMap[currentPitcher.throwHand] ?? "right",
      playerName:
        `${currentPitcher.lastName}, ${currentPitcher.firstName}`.trim(),
      position: "pitcher" as const,
    };

    saveManualTag(
      {
        body: {
          payload: {
            event_uuid: recentEvent.event_uuid,
            event_time: recentEvent.event_time,
            machine_uuid: machineId,
            tag_data: {
              metadata: {
                ...tagData,
              },
              gameState: {
                ...(gameState as GameState),
                currentPitcher: pitcherEntry,
              },
            },
          },
        },
      },
      {
        onSuccess: (data) => {
          dispatch(
            updateQueueEvent({
              ...recentEvent,
              tagData,
              focusedPlayers: {
                focusedPitcher: currentPitcher,
              },
              gameState: gameState as IGameState,
            }),
          );
          dispatch(setStaleEvents(recentEvent.contributing_events || []));
          dispatch(resetAllTags(undefined));
          if (data.gameState && data.game) {
            updateGameState(data.gameState, data.game);
          }
          toast("Pitch tag saved!", { variant: "success" });
        },
      },
    );
  };

  // Pitcher assignment / replacement (unified: "out" is null when no pitcher was previously assigned)
  const handlePitcherSubmit = async (player: IPlayer, _teamUuid: string) => {
    if (!gameUuid) return;

    const inEntry = mapIPlayerToLineupEntry(player, "pitcher");
    const out = currentPitcher?.uuid
      ? await fetchSavedLineupEntry(
          "home",
          mapIPlayerToLineupEntry(currentPitcher, "pitcher"),
        )
      : null;

    substitutePitcher(
      {
        body: {
          in: inEntry,
          out,
          side: "home",
        },
        path: {
          game_id: gameUuid,
        },
      },
      {
        onSuccess: (data) => {
          dispatch(setBullpenPitcher(player));
          dispatch(resetBullpenPitchCount());
          setReplacePitcherOpen(false);
          toast(
            `${player.firstName} ${player.lastName} is now the bullpen pitcher!`,
            { variant: "success" },
          );
          if (data.gameState && data.game) {
            updateGameState(data.gameState, data.game, true);
          }
        },
      },
    );
  };

  return (
    <>
      <div
        className={clsx(
          // Size
          "min-h-[calc(100vh-var(--navbar-height))]",
          // Layout
          "flex flex-col items-center",
          // Spacing
          "gap-4 px-6 sm:px-4 py-4",
        )}
      >
        {/* Row 1: Pitcher Tile */}
        <div className="flex w-full justify-center">
          <ActivePlayerTile
            playerType="pitcher"
            player={currentPitcher}
            pitchCount={pitchCount}
            handleClick={() => setReplacePitcherOpen(true)}
            clickable
          />
        </div>

        {/* Row 2: Main Content */}
        <div className="flex w-full gap-4 flex-wrap lg:flex-nowrap">
          {/* Left Column: Pitch Type Buttons + Visualization */}
          <div className="flex flex-col gap-4 min-w-[400px] flex-1">
            <BullpenPitchTypeButtons />
            <VisualizationContainer containerWidth="100%" />
          </div>

          {/* Right Column: Data Tiles */}
          <div className="flex-1 min-w-[300px]">
            <TilesContainer direction="grid" tileType="pitching" />
          </div>
        </div>

        {/* Row 3: Action Buttons */}
        <div className="flex w-full justify-between">
          <div className="flex gap-2">
            <Button
              variant="secondary-flat-light"
              size="default"
              onClick={() =>
                dispatch(setModalState({ state: "Change Sport", data: {} }))
              }
            >
              Change Sport
            </Button>
            <Button
              variant="secondary-flat-light"
              size="default"
              onClick={() =>
                dispatch(setModalState({ state: "End Session", data: {} }))
              }
            >
              End Bullpen
            </Button>
          </div>
          <Button
            variant="secondary-flat-light"
            size="default"
            onClick={handleManualSave}
            isLoading={isManualSaving}
            isDisabled={!recentEvent?.event_uuid || !currentPitcher?.uuid}
          >
            Save Pitch Tag
          </Button>
        </div>
      </div>

      {/* Select/Replace Pitcher Modal */}
      <SelectPitcherModal
        isOpen={replacePitcherOpen}
        onClose={() => setReplacePitcherOpen(false)}
        onSubmit={handlePitcherSubmit}
        isLoading={isSubstituting}
        title={
          !currentPitcher?.uuid
            ? "Select Bullpen Pitcher"
            : "Replace Bullpen Pitcher"
        }
        submitLabel={
          !currentPitcher?.uuid ? "Select Pitcher" : "Replace Pitcher"
        }
      />
    </>
  );
};
